import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function HeroSection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20background%20with%20flowing%20gradients%20of%20warm%20terracotta%20sand%20and%20soft%20charcoal%20grey%2C%20organic%20fluid%20shapes%20merging%20like%20watercolor%20washes%2C%20subtle%20textile%20fabric%20texture%20overlay%2C%20no%20text%2C%20modern%20contemporary%20art%20style%2C%20elegant%20sophisticated%20mood%2C%20dark%20rich%20tonal%20contrast%2C%20high%20resolution%20abstract%20wallpaper&width=1800&height=1200&seq=hero-bg-cssbuy-v2-b01&orientation=landscape"
          alt="CSSBuy Spreadsheet background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-6 py-32 md:py-40 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/15 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
          <span className="text-xs md:text-sm text-background-50 font-medium">Guia CSSBuy Spreadsheet 2026</span>
        </div>

        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-6 drop-shadow-lg">
          CSSBuy Spreadsheet
          <br />
          <span className="text-background-50/90 font-normal italic text-2xl md:text-4xl lg:text-5xl">
            Guia Completo para Comprar da China
          </span>
        </h1>

        <p className="text-sm md:text-base text-background-50/85 max-w-2xl mx-auto mb-10 leading-relaxed drop-shadow">
          {t("heroDesc")}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
          >
            {t("btnCssbuySpreadsheet")} <i className="ri-arrow-right-up-line ml-1.5"></i>
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-background-50 text-foreground-900 text-sm md:text-base font-semibold hover:bg-background-50/95 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("btnBuyNow")} <i className="ri-shopping-bag-line ml-1.5"></i>
          </a>
        </div>

        <div className="relative inline-block" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/25 text-background-50 text-sm md:text-base hover:bg-background-50/25 transition-all whitespace-nowrap cursor-pointer font-medium"
          >
            <i className="ri-grid-line"></i>
            <span>{t("categorySelect")}</span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
          </button>
          {dropdownOpen && (
            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-80 bg-background-50 rounded-xl overflow-hidden shadow-lg z-50 max-h-96 overflow-y-auto">
              <div className="p-2">
                <p className="px-4 py-2 text-xs text-foreground-400 font-medium uppercase tracking-wider">Todas as Categorias</p>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-3"
                  >
                    <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-xs flex-shrink-0">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-foreground-900">{cat.namePt}</span>
                      <span className="text-xs text-foreground-400">{cat.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a
          href="#categories"
          className="flex flex-col items-center gap-2 text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
        >
          <span className="text-xs font-medium">Explorar</span>
          <i className="ri-arrow-down-line animate-bounce text-lg"></i>
        </a>
      </div>
    </section>
  );
}